import { create } from 'zustand'
import { Usuario } from '../types'

interface AuthState {
  token: string | null
  usuario: Usuario | null
  /** Indica que o estado foi lido do localStorage — evita flash de tela de login no refresh. */
  hydrated: boolean
  /** Persiste token e usuário no localStorage e atualiza o store (chamado após login). */
  setAuth: (token: string, usuario: Usuario) => void
  /** Remove credenciais do localStorage e limpa o store (chamado em logout ou 401). */
  logout: () => void
  /** Retorna true se o usuário logado tem função 'Admin'. */
  isAdmin: () => boolean
  /**
   * Verifica se o usuário possui uma permissão específica.
   * Prefira usar o hook `usePermission` em componentes React.
   *
   * @example
   * const { canDo } = useAuthStore()
   * if (canDo('membros', 'deletar')) { ... }
   */
  canDo: (recurso: string, acao: string) => boolean
  hydrate: () => void
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    // JWT exp é em segundos; Date.now() é em milissegundos
    return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

/**
 * Store de autenticação (Zustand).
 * Inicializa lendo token e usuário do localStorage para persistir a sessão entre recarregamentos.
 * O parse do usuário é protegido por try/catch para tolerância a dados corrompidos.
 * Tokens expirados são descartados na hidratação.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  token: (() => {
    const t = localStorage.getItem('ct_token')
    if (!t || isTokenExpired(t)) {
      localStorage.removeItem('ct_token')
      localStorage.removeItem('ct_usuario')
      return null
    }
    return t
  })(),
  usuario: (() => {
    try {
      const t = localStorage.getItem('ct_token')
      if (!t || isTokenExpired(t)) return null
      const u = localStorage.getItem('ct_usuario')
      return u ? JSON.parse(u) : null
    } catch { return null }
  })(),
  hydrated: true,
  setAuth: (token, usuario) => {
    localStorage.setItem('ct_token', token)
    localStorage.setItem('ct_usuario', JSON.stringify(usuario))
    set({ token, usuario })
  },
  logout: () => {
    localStorage.removeItem('ct_token')
    localStorage.removeItem('ct_usuario')
    set({ token: null, usuario: null })
  },
  isAdmin: () => get().usuario?.funcao === 'Admin',
  /**
   * Verifica uma permissão fora de componentes React (ex: interceptors, guards).
   * Em componentes, prefira o hook `usePermission(recurso, acao)`.
   */
  canDo: (recurso, acao) => {
    const permissoes = get().usuario?.permissoes ?? []
    return permissoes.includes(`${recurso}:${acao}`)
  },
  hydrate: () => set({ hydrated: true }),
}))
